import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mic, Send, Square, X, Loader2, Image as ImageIcon } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useRef, useState, useEffect } from "react";
import { toast } from "sonner";

interface ChatInputProps {
  isVoiceMode: boolean;
  isRecording: boolean;
  isConverting?: boolean;
  recordingTime: number;
  newMessage: string;
  isLoading: boolean;
  dailyMessageCount: number;
  dailyVoiceCount: number;
  dailyMessageLimit: number;
  dailyVoiceLimit: number;
  dailyImageCount: number;
  dailyImageLimit: number;
  setNewMessage: (message: string) => void;
  handleSendMessage: (e: React.FormEvent) => void;
  startRecording: () => void;
  stopRecording: () => void;
  formatTime: (seconds: number) => string;
  onImageSelect: (file: File) => void;
  selectedImage: File | null;
  clearSelectedImage: () => void;
}

export const ChatInput = ({
  isVoiceMode,
  isRecording,
  isConverting = false,
  recordingTime,
  newMessage,
  isLoading,
  dailyMessageCount,
  dailyVoiceCount,
  dailyMessageLimit,
  dailyVoiceLimit,
  dailyImageCount,
  dailyImageLimit,
  setNewMessage,
  handleSendMessage,
  startRecording,
  stopRecording,
  formatTime,
  onImageSelect,
  selectedImage,
  clearSelectedImage,
}: ChatInputProps) => {
  const isMobile = useIsMobile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localImagePreviewUrl, setLocalImagePreviewUrl] = useState<string | null>(null);

  const isVoiceLimitReached = dailyVoiceCount >= dailyVoiceLimit;
  const isMessageLimitReached = dailyMessageCount >= dailyMessageLimit;
  const isImageLimitReached = dailyImageCount >= dailyImageLimit;

  const handleImageClick = () => {
    if (isImageLimitReached) {
      toast("Giới hạn hình ảnh đã đạt. Bạn đã đạt giới hạn gửi hình ảnh hôm nay");
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onImageSelect(file);
      const url = URL.createObjectURL(file);
      setLocalImagePreviewUrl(url);
    }
    e.target.value = '';
  };

  const handleLocalClearImage = () => {
    clearSelectedImage();
    if (localImagePreviewUrl) {
      URL.revokeObjectURL(localImagePreviewUrl);
      setLocalImagePreviewUrl(null);
    }
  };

  const handleLocalSubmit = (e: React.FormEvent) => {
    handleSendMessage(e);
    handleLocalClearImage();
  };

  useEffect(() => {
    if (!selectedImage && localImagePreviewUrl) {
      handleLocalClearImage();
    }
  }, [selectedImage]);

  useEffect(() => {
    return () => {
      if (localImagePreviewUrl) {
        URL.revokeObjectURL(localImagePreviewUrl);
      }
    };
  }, [localImagePreviewUrl]);

  return (
    <form
      onSubmit={handleLocalSubmit}
      className="p-2 sm:p-4"
    >
      {isVoiceMode ? (
        isRecording ? (
          <div className="flex-1 flex items-center gap-2 bg-primary rounded-lg px-2 sm:px-3 py-1 sm:py-2">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => stopRecording()}
              className="h-6 w-6 sm:h-8 sm:w-8 p-0 hover:bg-primary-foreground/20"
            >
              <X className="h-3 w-3 sm:h-4 sm:w-4 text-primary-foreground" />
            </Button>
            <div className="flex-1 flex items-center justify-between">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => stopRecording()}
                className="h-6 w-6 sm:h-8 sm:w-8 p-0 hover:bg-primary-foreground/20"
              >
                <Square className="h-3 w-3 sm:h-4 sm:w-4 text-primary-foreground" />
              </Button>
              <span className="text-xs sm:text-sm font-medium text-primary-foreground">
                {formatTime(recordingTime)}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => stopRecording()}
                className="h-6 w-6 sm:h-8 sm:w-8 p-0 hover:bg-primary-foreground/20"
              >
                <Send className="h-3 w-3 sm:h-4 sm:w-4 text-primary-foreground" />
              </Button>
            </div>
          </div>
        ) : (
          <Button
            type="button"
            onClick={startRecording}
            className="bg-primary hover:bg-primary/90 text-primary-foreground flex-shrink-0 h-8 sm:h-10 w-8 sm:w-10 p-0"
            disabled={isMessageLimitReached || isVoiceLimitReached || isConverting}
          >
            {isConverting ? (
              <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
            ) : (
              <Mic className="w-3 h-3 sm:w-4 sm:h-4" />
            )}
            <span className="sr-only">
              {isConverting ? "Đang chuyển đổi âm thanh..." : "Bắt đầu ghi âm"}
            </span>
          </Button>
        )
      ) : (
        <div className="flex gap-2">
          <div className="flex-1 flex gap-2">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={isMessageLimitReached 
                ? `Đã đạt giới hạn hôm nay` 
                : "Nhập tin nhắn của bạn..."}
              className="flex-1 bg-background/50 border-border/50 focus:ring-primary h-8 sm:h-10 text-sm"
              disabled={isLoading || isMessageLimitReached || isRecording || isConverting}
            />
            {localImagePreviewUrl && (
              <div className="relative">
                <img 
                  src={localImagePreviewUrl} 
                  alt="Preview" 
                  className="h-8 sm:h-10 w-auto rounded border border-border/50"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handleLocalClearImage}
                  className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-destructive hover:bg-destructive/90 p-0"
                >
                  <X className="h-3 w-3 text-destructive-foreground" />
                </Button>
              </div>
            )}
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*"
            className="hidden"
          />
          <Button
            type="button"
            onClick={handleImageClick}
            className="bg-primary hover:bg-primary/90 text-primary-foreground h-8 sm:h-10 w-8 sm:w-10 p-0"
            disabled={isLoading || isMessageLimitReached || isRecording || isConverting || selectedImage !== null || isImageLimitReached}
          >
            <ImageIcon className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="sr-only">Upload image</span>
          </Button>
          <Button 
            type="submit" 
            disabled={isLoading || isMessageLimitReached || isRecording || isConverting}
            className="bg-primary hover:bg-primary/90 text-primary-foreground h-8 sm:h-10 w-8 sm:w-10 p-0"
          >
            <Send className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="sr-only">Gửi tin nhắn</span>
          </Button>
        </div>
      )}
    </form>
  );
};
